import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  COMM_CHANNELS,
  CONVERSATION_ENTITY_TYPES,
  CONVERSATION_STATUSES,
  channelMeta,
  conversationEntityMeta,
  conversationStatusMeta,
  createConversationMessage,
  createCoreConversation,
  fetchConversationStats,
  listConversationMessages,
  listCoreConversations,
  listAiClientThreadsForOrganization,
  softDeleteCoreConversation,
  updateCoreConversation,
  type AiClientThreadSummary,
  type ConversationChannel,
  type CoreConversation,
  type CoreConversationMessage,
} from '@/lib/coreCommunication';
import AiClientThreadModal from './AiClientThreadModal';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function CommunicationTab({ org }: Props) {
  const { user } = useAuth();
  const [items, setItems] = useState<CoreConversation[]>([]);
  const [stats, setStats] = useState<{ total: number; open: number; byChannel: Record<string, number> }>({ total: 0, open: 0, byChannel: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: 'internal' as ConversationChannel, subject: '', entityType: '', entityId: '' });
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState<CoreConversation | null>(null);
  const [messages, setMessages] = useState<CoreConversationMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [aiThreads, setAiThreads] = useState<AiClientThreadSummary[]>([]);
  const [aiClientId, setAiClientId] = useState<number | null>(null);
  const [aiContext, setAiContext] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, st] = await Promise.all([listCoreConversations(org.id), fetchConversationStats(org.id)]);
      setItems(list);
      setStats(st);
      try {
        setAiThreads(await listAiClientThreadsForOrganization(org.id));
      } catch {
        setAiThreads([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    // Les conversations liées à l'assistant IA sont présentées dans une section
    // dédiée (« Fils Assistant IA ») : on les retire de la liste classique.
    const base = items.filter((c) => c.entity_type !== 'ai_client');
    return statusFilter === 'all' ? base : base.filter((c) => c.status === statusFilter);
  }, [items, statusFilter]);

  const submit = async () => {
    if (!form.subject.trim()) return;
    setSaving(true);
    try {
      await createCoreConversation({
        tenantId: org.tenant_id,
        organizationId: org.id,
        channel: form.channel,
        subject: form.subject.trim(),
        entityType: form.entityType || null,
        entityId: form.entityId.trim() || null,
      });
      setOpen(false);
      setForm({ channel: 'internal', subject: '', entityType: '', entityId: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (c: CoreConversation) => {
    setActive(c);
    setDraft('');
    try {
      setMessages(await listConversationMessages(c.id));
    } catch {
      setMessages([]);
    }
  };

  const send = async () => {
    if (!active || !draft.trim()) return;
    setSaving(true);
    try {
      const dup = draft.trim();
      setDraft('');
      await createConversationMessage({
        conversationId: active.id,
        tenantId: org.tenant_id,
        organizationId: org.id,
        channel: active.channel,
        direction: 'outbound',
        authorType: 'agent',
        authorUserId: user?.id ?? null,
        authorName: user?.name || 'Vous',
        body: dup,
      });
      setMessages(await listConversationMessages(active.id));
      load();
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (c: CoreConversation, status: string) => {
    setBusyId(c.id);
    try { await updateCoreConversation(c.id, { status }); load(); } finally { setBusyId(null); }
  };

  const remove = async (c: CoreConversation) => {
    setBusyId(c.id);
    try { await softDeleteCoreConversation(c.id, user?.id ?? null); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Communication Core : un système commun réutilisé par <span className="font-semibold text-foreground-700">CRM, Support, Booking, Sales et Marketing</span>.
          Chaque conversation est reliée à une entité (client, entreprise, commande, réservation, RDV, ticket).
        </p>
        <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle conversation
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {COMM_CHANNELS.map((c) => (
          <div key={c.key} className={`rounded-lg border p-3 ${c.available ? 'bg-background-50 border-background-200/70' : 'bg-background-100/60 border-background-200/50'}`}>
            <div className="flex items-center justify-between">
              <i className={`${c.icon} text-lg ${c.available ? 'text-primary-600' : 'text-foreground-300'}`}></i>
              <span className="text-sm font-bold text-foreground-900">{stats.byChannel[c.key] || 0}</span>
            </div>
            <p className="text-xs font-medium text-foreground-700 mt-1.5">{c.label}</p>
            {!c.available && <p className="text-[10px] text-foreground-400">Intégration à venir</p>}
          </div>
        ))}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Fils Assistant IA des clients — démarrés côté client, consultables ici */}
      {aiThreads.length > 0 && (
        <div className="bg-background-50 border border-accent-200/60 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-robot-2-line"></i></span>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-foreground-900">Fils Assistant IA (clients)</h4>
              <p className="text-[11px] text-foreground-500">Des clients ont cherché votre offre via l’assistant — ouvrez le fil et répondez directement.</p>
            </div>
          </div>
          <div className="divide-y divide-background-200/60">
            {aiThreads.map((t) => (
              <div key={t.conversationId} className="flex items-center gap-3 px-4 py-3">
                <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0"><i className="ri-robot-2-line"></i></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-800 truncate">Client #{t.clientId}</p>
                  <p className="text-[11px] text-foreground-400">
                    {t.lastMessageAt ? new Date(t.lastMessageAt).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Nouveau fil'}
                  </p>
                </div>
                <button onClick={() => { setAiContext(null); setAiClientId(t.clientId); }} className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer">
                  <i className="ri-chat-forward-line"></i> Ouvrir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          <button onClick={() => setStatusFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${statusFilter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            Toutes ({stats.total})
          </button>
          {CONVERSATION_STATUSES.map((s) => (
            <button key={s.key} onClick={() => setStatusFilter(s.key)} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${statusFilter === s.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-chat-3-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune conversation.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => {
            const ch = channelMeta(c.channel);
            const st = conversationStatusMeta(c.status);
            const ent = conversationEntityMeta(c.entity_type);
            return (
              <div key={c.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
                <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${ch.className}`}><i className={`${ch.icon} text-lg`}></i></span>
                <button onClick={() => openDetail(c)} className="min-w-0 flex-1 text-left cursor-pointer">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-900 truncate">{c.subject || 'Sans objet'}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.className}`}>{st.label}</span>
                    {ent && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap"><i className={ent.icon}></i>{ent.label} #{c.entity_id}</span>}
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1">
                    {ch.label}
                    {c.last_message_at ? ` · ${new Date(c.last_message_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                  </p>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {c.status !== 'closed' ? (
                    <button onClick={() => changeStatus(c, 'closed')} disabled={busyId === c.id} title="Fermer" className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-check-double-line"></i></button>
                  ) : (
                    <button onClick={() => changeStatus(c, 'open')} disabled={busyId === c.id} title="Réouvrir" className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-arrow-go-back-line"></i></button>
                  )}
                  <button onClick={() => remove(c)} disabled={busyId === c.id} title="Supprimer" className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title="Nouvelle conversation"
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={submit} disabled={saving || !form.subject.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Créer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Canal</span>
              <select className={INPUT} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value as ConversationChannel })}>
                {COMM_CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}{c.available ? '' : ' (à venir)'}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Objet</span>
              <input className={INPUT} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Ex : Question sur la réservation" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité liée</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
                  <option value="">— Aucune —</option>
                  {CONVERSATION_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant</span>
                <input className={INPUT} value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} placeholder="Ex : 100234" disabled={!form.entityType} />
              </label>
            </div>
          </div>
        </Modal>
      )}

      {active && (
        <Modal
          title={active.subject || 'Conversation'}
          onClose={() => setActive(null)}
          maxWidth="max-w-2xl"
          footer={
            <div className="flex items-center gap-2 w-full">
              <input
                className={INPUT}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Écrire un message…"
              />
              <button onClick={send} disabled={saving || !draft.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"><i className="ri-send-plane-line"></i> Envoyer</button>
            </div>
          }
        >
          <div className="space-y-3">
            {messages.length === 0 ? (
              <p className="text-xs text-foreground-400 text-center py-6">Aucun message pour l’instant. Lancez la conversation 👇</p>
            ) : (
              messages.map((m) => {
                const mine = m.direction === 'outbound';
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-800'}`}>
                      <p className="text-xs font-semibold opacity-80 mb-0.5">{m.author_name || (mine ? 'Agent' : 'Contact')}</p>
                      <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${mine ? 'text-background-100/80' : 'text-foreground-400'}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Modal>
      )}

      {/* Fil IA client : consultation + réponse depuis la Communication */}
      {aiClientId !== null && (
        <AiClientThreadModal
          organizationId={org.id}
          tenantId={org.tenant_id}
          clientId={aiClientId}
          contextLabel={aiContext}
          onClose={() => setAiClientId(null)}
          onReplied={load}
        />
      )}
    </div>
  );
}